import { Field, FormSpy } from "react-final-form";
import { Divider, Modal, message } from "antd";
import { RadioField, TitlePage, PhoneField, CustomButton, Icon } from "~/components";
import { composeValidators, disabledValidate, required, testRegExp } from "~/utils/validate";
import { FormField, PayformNameField, PaymentMethodsSelectField, SocialLinkWithModalField } from "~/pages/questionnaires/shared";
import { TextareaModalField } from "~/new-ui-customer-questionnaire/components/TextareaModalField/TextareaModalField";
import { NamesOfFieldEnum } from "~/typings/types";
import cn from 'classnames';
import { configTitleFieldsOfIndividualEntrepreneur } from "../configTitleFields";
import { V2Wizard, PagePreviewModal, FieldGenerateLogo } from "../../../../shared";
import { FullWidthRowFormContainer, RowFieldWithButtonsContainer } from "~/pages/questionnaires/shared/form-field";
import styles from "./CommonSteps.module.scss";
import { useScrollAndFocusField } from "~/hooks";
import { useAnalyticsContext } from "~/services/analytics";
import { useEffect, useRef, useState } from "react";

const getProductDescriptionConfig = (productType: string) => {
  switch (productType) {
    case "goods":
      return {
        title: "Опишите товары, которые сейчас продаёте",
        placeholder: "Расскажите о ваших товарах просто и понятно. Опишите, какую пользу получает клиент и какую задачу решает ваш продукт."
      };
    case "education":
      return {
        title: "Опишите курсы, которые сейчас проводите",
        placeholder: "Расскажите о ваших курсах просто и понятно. Опишите, какую пользу получает клиент и какую задачу решает ваш курс."
      };
    default:
      return {
        title: "Расскажите о вашем продукте или услуге",
        placeholder: "Расскажите о ваших услугах просто и понятно. Опишите, какую пользу получает клиент и какую задачу решает ваш продукт."
      };
  }
};

export const PayformTechDetails = ({
  paymentMethod,
  educationalPlatform,
  productType,
  typeRequestCheckPayformName,
  setTypeRequestCheckPayformName,
  callbackCheckPayformName,
  isLoadingMount,
  setLogoFileId,
  logoError,
  setLogoError,
  errorMsgCheckPayformName,
  isFirstRenderPayformTechDetailsStep,
  setIsFirstRenderPayformTechDetailsStep,
  clientId,
  logoData,
  setLogoData,
  setIsLogoProcessing
}: any) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentLogoSrc, setCurrentLogoSrc] = useState('');
  const [productTypeChangeConfirm, setProductTypeChangeConfirm] = useState<{ newValue: string; form: any } | null>(null);

  const isInitDone = useRef(false);

  useScrollAndFocusField(NamesOfFieldEnum.ProductName);

  const analytics = useAnalyticsContext();
  analytics?.trackStep('anketa-customer-ie-init_step4');
  analytics?.trackStepWebvisor('webvisor_anketa_customer_step3');

  useEffect(() => {
    setIsFirstRenderPayformTechDetailsStep(true);
  }, []);

  return (
    <V2Wizard.Page>
      <TitlePage
        name="Настройка платёжной страницы"
        number="4"
      />
      <Divider style={{ margin: 0 }} />
      <div className={styles.container}>
        <FormSpy subscription={{ values: true }}>
          {({ values, form }) => {
            if (isFirstRenderPayformTechDetailsStep && !isInitDone.current) {
              isInitDone.current = true;

              form.change(
                NamesOfFieldEnum.PayformEmail,
                values?.[NamesOfFieldEnum.CrmEmail]
              );
              form.change(
                NamesOfFieldEnum.PayformPhone,
                values?.[NamesOfFieldEnum.CrmPhone]
              );

              if (paymentMethod) {
                const current = values?.[NamesOfFieldEnum.PayformPaymentMethods];
                if (!current || current.length === 0) {
                  const allNames = Object.keys(paymentMethod);
                  form.change(NamesOfFieldEnum.PayformPaymentMethods, allNames);
                }
              }
            }
            return null;
          }}
        </FormSpy>

        {productType && Object.keys(productType).length && (
          <FullWidthRowFormContainer>
            <FormSpy subscription={{ values: true }}>
              {({ values: spyValues, form: spyForm }) => (
                <FormField
                  titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.ProductType]}
                  name={NamesOfFieldEnum.ProductType}
                  tooltip="Выберите, что будете продавать. Если ваш продукт подходит к нескольким вариантам, то выберите тот, где продаж больше всего"
                  isColon={false}
                  required
                  shouldRenderTitleAboveField
                  typeField="radio"
                >
                  <div className={styles.containerRadio}>
                    {Object.values(productType).map(({ name, title }: any) => (
                      <Field
                        key={name}
                        name={NamesOfFieldEnum.ProductType}
                        type="radio"
                        value={name}
                        validate={disabledValidate(composeValidators(required()))}
                      >
                        {({ input }) => (
                          <RadioField
                            input={{
                              ...input,
                              onChange: () => {
                                const hasDescription = !!(spyValues[NamesOfFieldEnum.ProductDescription] as string);
                                if (hasDescription && spyValues[NamesOfFieldEnum.ProductType] !== name) {
                                  setProductTypeChangeConfirm({ newValue: name, form: spyForm });
                                } else {
                                  input.onChange(name);
                                }
                              },
                            }}
                          >{title}</RadioField>
                        )}
                      </Field>
                    ))}
                  </div>

                  {productTypeChangeConfirm !== null && (
                    <Modal
                      open
                      title="Изменение типа деятельности"
                      onCancel={() => setProductTypeChangeConfirm(null)}
                      footer={
                        <div style={{ display: 'flex', gap: 16 }}>
                          <CustomButton
                            name="Отмена"
                            type="button"
                            onClick={() => setProductTypeChangeConfirm(null)}
                            className={cn(styles.buttonBackWarningModal, styles.buttonWarningModal)}
                          />
                          <CustomButton
                            name="Продолжить"
                            type="button"
                            onClick={() => {
                              productTypeChangeConfirm.form.change(NamesOfFieldEnum.ProductType, productTypeChangeConfirm.newValue);
                              productTypeChangeConfirm.form.change(NamesOfFieldEnum.ProductDescription, "");
                              setProductTypeChangeConfirm(null);
                            }}
                            className={styles.buttonWarningModal}
                          />
                        </div>
                      }
                    >
                      <p>При смене типа деятельности описание будет сброшено. Продолжить?</p>
                      {spyValues[NamesOfFieldEnum.ProductDescription] && (
                        <div style={{ marginTop: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px' }}>
                          <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Текущее описание:</span>
                          <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.55, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}>{spyValues[NamesOfFieldEnum.ProductDescription] as string}</p>
                        </div>
                      )}
                    </Modal>
                  )}
                </FormField>
              )}
            </FormSpy>
          </FullWidthRowFormContainer>
        )}

        <FullWidthRowFormContainer>
          <FormSpy subscription={{ values: true }}>
            {propsSpy => (
              <>
                {propsSpy.values[NamesOfFieldEnum.ProductType] === "education" && (
                  <FormField
                    titleField={
                      configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.IsHaveEducationalLicense]
                    }
                    name={NamesOfFieldEnum.IsHaveEducationalLicense}
                    isColon={false}
                    required
                    styleContainerTitleField={{ width: "96%" }}
                    shouldRenderTitleAboveField
                  >
                    <div className={styles.containerRadio}>
                      <Field
                        name={NamesOfFieldEnum.IsHaveEducationalLicense}
                        type="radio"
                        value="yes"
                        component={props => <RadioField   {...props} />}
                        validate={disabledValidate(composeValidators(required()))}
                      >
                        Да
                      </Field>
                      <Field
                        name={NamesOfFieldEnum.IsHaveEducationalLicense}
                        type="radio"
                        value="no"
                        component={props => <RadioField   {...props} />}
                        validate={disabledValidate(composeValidators(required()))}
                      >
                        Нет
                      </Field>
                    </div>
                  </FormField>
                )}
                {propsSpy.values[NamesOfFieldEnum.ProductType] === "goods" && (
                  <FormField
                    titleField={
                      configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.IsNeedOnlineKassa]
                    }
                    name={NamesOfFieldEnum.IsNeedOnlineKassa}
                    isColon={false}
                    styleContainerTitleField={{ width: "96%" }}
                    shouldRenderTitleAboveField
                    tooltip="Онлайн-касса от Продамус для регистрации безналичных платежей и формирование кассовых чеков. Соответствует всем требованиям закона 54-ФЗ. Касса обязательна при продаже товаров и оказании услуг"
                    typeField="radio"
                  >
                    <div className={styles.containerRadio}>
                      <Field
                        name={NamesOfFieldEnum.IsNeedOnlineKassa}
                        type="radio"
                        value="yes"
                        component={props => <RadioField {...props} />}
                      >
                        Да
                      </Field>
                      <Field
                        name={NamesOfFieldEnum.IsNeedOnlineKassa}
                        type="radio"
                        value="no"
                        component={props => <RadioField {...props} />}
                      >
                        Нет
                      </Field>
                    </div>
                  </FormField>
                )}
              </>
            )}
          </FormSpy>
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <RowFieldWithButtonsContainer
            fields={[
              <FormField
                titleField={'Название вашего проекта, которое будут видеть покупатели'}
                name={NamesOfFieldEnum.ProductName}
                required
                validate={composeValidators(required())}
              />
            ]}
            buttons={[
            ]}
          />
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <FormSpy subscription={{ values: true }}>
            {({ values }) => {
              const productType = values[NamesOfFieldEnum.ProductType];
              const config = getProductDescriptionConfig(productType);
              const hintItemsMap: Record<string, Array<{ title: string; description: string }>> = {
                goods: [
                  { title: "Категория товаров", description: "Укажите категорию: детские игрушки, одежда, аксессуары, типография и т.д.\nОпишите 2–3 предложениями, что именно продаёте." },
                  { title: "Способ доставки", description: "Как доставляете заказы?\nНапример: Почта России, курьерская служба, пункт выдачи, самовывоз." },
                  { title: "Сроки отправки", description: "Через сколько дней после оплаты отправляете заказ?\nНапример: от 1 до 3 рабочих дней после поступления оплаты." },
                ],
                services: [
                  { title: "Направление", description: "Укажите сферу деятельности: красота, психология, маркетинг, нутрициология, юридические услуги и т.д." },
                  { title: "Формат оказания", description: "Как проходит услуга - офлайн или онлайн? Индивидуально или в группе?" },
                  { title: "Способ предоставления", description: "Как клиент получает услугу?\nНапример: личное посещение, видеозвонок, доступ к файлам по ссылке, ведение аккаунта." },
                ],
                education: [
                  { title: "Тема и направление", description: "Укажите сферу: красота, программирование, психология и т.д.\nОпишите суть курса в 2–3 предложениях." },
                  { title: "Формат курсов", description: "Как проходит обучение? Онлайн или офлайн, в группе или индивидуально?" },
                  { title: "Способ предоставления материалов", description: "Где ученик получает доступ к материалам?\nНапример: личный кабинет на платформе, сообщество ВК, доступ к файлам по ссылке, почтовая рассылка." },
                ],
              };
              const exampleMap: Record<string, { lines: Array<{ key: string; value: string }> }> = {
                goods: {
                  lines: [
                    { key: "Категория товаров", value: "детские игрушки - продажа настольных игр, мягких игрушек и конструкторов для детей от 3 лет. Все изделия из сертифицированных материалов." },
                    { key: "Способ доставки", value: "Почта России по всей России." },
                    { key: "Сроки отправки", value: "от 1 до 3 рабочих дней после поступления оплаты." },
                  ]
                },
                services: {
                  lines: [
                    { key: "Направление", value: "ремонт смартфонов, ноутбуков, планшетов (замена экранов, батарей, ПО-восстановление)." },
                    { key: "Формат оказания", value: "личная услуга оффлайн." },
                    { key: "Способ предоставления", value: "личное посещение (выезд или сервисный центр)." },
                  ]
                },
                education: {
                  lines: [
                    { key: "Тема и направление", value: "программирование - курс Python для начинающих и среднего уровня. Изучаем основы языка, алгоритмы и написание реальных проектов." },
                    { key: "Формат курсов", value: "онлайн, группы до 10 человек, занятия 2 раза в неделю." },
                    { key: "Способ предоставления материалов", value: "личный кабинет на платформе Prodamus.XL, доступ к записям сохраняется 1 год после окончания курса." },
                  ]
                },
              };
              return (
                <Field name={NamesOfFieldEnum.ProductDescription} validate={composeValidators(required())}>
                  {({ input, meta }) => (
                    <TextareaModalField
                      name={NamesOfFieldEnum.ProductDescription}
                      label={config.title}
                      value={input.value ?? ""}
                      onChange={input.onChange}
                      placeholder={config.placeholder}
                      maxLength={10000}
                      required
                      error={meta.submitFailed && meta.error ? meta.error : undefined}
                      hintItems={hintItemsMap[productType] ?? []}
                      example={exampleMap[productType]}
                      modalTitle="Описание деятельности"
                      triggerWhiteBg
                      saveBtnGreen
                      isOldUI
                      onBlockedClick={!productType ? () => message.warning(`Чтобы продолжить, нужно выбрать "${configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.ProductType]}"`) : undefined}
                    />
                  )}
                </Field>
              );
            }}
          </FormSpy>
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <PayformNameField
            clientId={clientId}
            titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.PayformName]}
            name={NamesOfFieldEnum.PayformName}
            required
            tooltip="Назовите платёжную ссылку просто и понятно. Это название будут видеть ваши покупатели, например: poshiv.payform.ru"
            typeRequestCheckPayformName={typeRequestCheckPayformName}
            setTypeRequestCheckPayformName={setTypeRequestCheckPayformName}
            errorMsgCheckPayformName={errorMsgCheckPayformName}
            onBlurCallback={callbackCheckPayformName}
          />
        </FullWidthRowFormContainer>

        <FormField
          titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.PayformPhone]}
          name={NamesOfFieldEnum.PayformPhone}
          required
          tooltip="Это обязательное требование платёжных систем. Номер должен быть действительным"
        >
          <Field
            name={NamesOfFieldEnum.PayformPhone}
            subscription={{ submitFailed: true, error: true, submitError: true, value: true }}
            validate={disabledValidate(
              composeValidators(
                required(),
                testRegExp(/^[+7]/, "Номер телефона должен начинаться на +7")
              )
            )}
            component={props => <PhoneField
              onlyCountries={["ru"]}
              required
              titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.PayformPhone]}
              {...props}
            />}
          />
        </FormField>

        <FormField
          titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.PayformEmail]}
          name={NamesOfFieldEnum.PayformEmail}
          required
          validate={composeValidators(required())}
        />

        <FullWidthRowFormContainer>
          <SocialLinkWithModalField
            titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.PayformLinks]}
            name={NamesOfFieldEnum.PayformLinks}
            required
          />
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <PaymentMethodsSelectField
            titleField={configTitleFieldsOfIndividualEntrepreneur[NamesOfFieldEnum.PayformPaymentMethods]}
            name={NamesOfFieldEnum.PayformPaymentMethods}
            required
            options={paymentMethod}
          />
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <FieldGenerateLogo
            clientId={clientId}
            isLoadingMount={isLoadingMount}
            setLogoFileId={setLogoFileId}
            logoError={logoError}
            setLogoError={setLogoError}
            currentLogoSrc={currentLogoSrc}
            setCurrentLogoSrc={setCurrentLogoSrc}
            logoData={logoData}
            setLogoData={setLogoData}
            setIsLogoProcessing={setIsLogoProcessing}
          />
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <CustomButton
            icon={<Icon name="eye" size="xs" className={styles.icon} />}
            name="Посмотреть свою платёжную страницу"
            description="(На основе заполненной информации)"
            onClick={() => setIsPreviewOpen(true)}
            iconPosition="before"
            className={styles.buttonPreviewPage}
          />
        </FullWidthRowFormContainer>

        <PagePreviewModal
          visible={isPreviewOpen}
          logoSrc={currentLogoSrc}
          onClose={() => setIsPreviewOpen(false)}
        />

      </div>
    </V2Wizard.Page>
  );
};
